let loadedMeasurementId = null;
let scriptInjected = false;
const DEVICE_DIAGNOSTICS_SESSION_KEY = 'analytics_device_diagnostics_sent_v1';

const isBrowser = () => typeof window !== 'undefined' && typeof document !== 'undefined';

const getDisableKey = (measurementId) => `ga-disable-${measurementId}`;

const normalizeConsent = (consentPreferences) => {
  if (!consentPreferences || typeof consentPreferences !== 'object') {
    return { analyticsUsage: false, analyticsDevice: false };
  }

  return {
    analyticsUsage: Boolean(consentPreferences.analyticsUsage),
    analyticsDevice: Boolean(consentPreferences.analyticsDevice),
  };
};

const isAnyAnalyticsAllowed = (consentPreferences) => {
  const consent = normalizeConsent(consentPreferences);
  return consent.analyticsUsage || consent.analyticsDevice;
};

const ensureGtagStub = () => {
  if (!isBrowser()) return;
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function gtag(){ window.dataLayer.push(arguments); };
    // Set consent defaults to denied BEFORE the GA4 script loads.
    // This prevents any data collection until the user explicitly consents.
    window.gtag('consent', 'default', {
      analytics_storage: 'denied',
      ad_storage: 'denied',
      wait_for_update: 500,
    });
  }
};

const injectGtagScript = (measurementId) => {
  if (!isBrowser() || scriptInjected) return;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  script.setAttribute('data-analytics', 'ga4');
  document.head.appendChild(script);
  scriptInjected = true;
};

export const initializeAnalytics = (measurementId) => {
  if (!measurementId || !isBrowser()) return;

  if (loadedMeasurementId === measurementId && window.gtag) return;

  injectGtagScript(measurementId);
  ensureGtagStub();

  window.gtag('js', new Date());
  window.gtag('config', measurementId, { send_page_view: false });
  loadedMeasurementId = measurementId;
};

export const setAnalyticsConsent = (measurementId, consentPreferences) => {
  if (!measurementId || !isBrowser()) return;

  const granted = isAnyAnalyticsAllowed(consentPreferences);

  if (granted) {
    initializeAnalytics(measurementId);
    window[getDisableKey(measurementId)] = false;
    if (window.gtag) {
      window.gtag('consent', 'update', { analytics_storage: 'granted' });
    }
    return;
  }

  window[getDisableKey(measurementId)] = true;
  if (window.gtag) {
    window.gtag('consent', 'update', { analytics_storage: 'denied' });
  }
};

export const trackPageView = (measurementId, pagePath, consentPreferences) => {
  if (!measurementId || !pagePath || !isBrowser() || !window.gtag) return;
  const consent = normalizeConsent(consentPreferences);
  if (!consent.analyticsUsage) return;
  if (window[getDisableKey(measurementId)]) return;

  window.gtag('event', 'page_view', {
    page_path: pagePath,
    page_location: window.location.href,
    page_title: document.title,
  });
};

const detectBrowserFamily = () => {
  const userAgent = (navigator.userAgent || '').toLowerCase();
  if (userAgent.includes('edg/')) return 'Edge';
  if (userAgent.includes('firefox/')) return 'Firefox';
  if (userAgent.includes('chrome/') && !userAgent.includes('edg/')) return 'Chrome';
  if (userAgent.includes('safari/') && !userAgent.includes('chrome/')) return 'Safari';
  return 'Other';
};

export const trackDeviceDiagnostics = (measurementId, consentPreferences) => {
  if (!measurementId || !isBrowser() || !window.gtag) return;
  const consent = normalizeConsent(consentPreferences);
  if (!consent.analyticsDevice) return;
  if (window[getDisableKey(measurementId)]) return;

  const alreadySent = sessionStorage.getItem(DEVICE_DIAGNOSTICS_SESSION_KEY) === 'true';
  if (alreadySent) return;

  window.gtag('event', 'device_diagnostics', {
    browser_family: detectBrowserFamily(),
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight,
    device_memory_gb: navigator.deviceMemory || null,
    platform: navigator.platform || 'unknown',
  });

  sessionStorage.setItem(DEVICE_DIAGNOSTICS_SESSION_KEY, 'true');
};
